/**
 * CapabilitiesCard — Settings → Flint AI ▸ Capabilities.
 *
 * Two layers the assistant can be configured by:
 *   - Skills: reusable instruction packs the agent follows. The engine exists
 *     (create/update/delete skill tools) but the human CRUD surface lands next;
 *     shown here as the planned home so the roadmap is visible.
 *   - Tools: groups of actions the agent may take. Per-tenant toggles need a
 *     capability table + gating (V1.x). Tier-1 (GST/e-invoice/audit) is shown
 *     locked-on — it's correctness, not a preference.
 *
 * Everything here is disabled-with-reason today (the codebase convention for
 * V1.x surfaces) — no fake controls.
 */
import { BookOpen, Lock, Globe, Terminal, ShoppingCart, Plug } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { StatusPill } from "@flint/ui";
import { FormCard } from "../form-card.tsx";

interface ToolGroup {
  Icon: LucideIcon;
  label: string;
  description: string;
  /** Tier-1 capabilities can't be turned off — shown locked-on. */
  locked?: boolean;
}

const TOOL_GROUPS: ReadonlyArray<ToolGroup> = [
  { Icon: ShoppingCart, label: "Sales & money actions", description: "Draft and issue invoices, record receipts and payments." },
  { Icon: Globe, label: "Web research", description: "Search the web and read pages to answer questions." },
  { Icon: Terminal, label: "Code sandbox", description: "Run scripts and work with files in a sandbox." },
  { Icon: Plug, label: "External tools (MCP)", description: "Connect outside tools the assistant can call." },
];

export function CapabilitiesCard() {
  return (
    <FormCard
      id="capabilities"
      icon={<BookOpen size={14} strokeWidth={2} aria-hidden />}
      title="Capabilities"
      description="Skills are playbooks the assistant follows. Tools are the actions it can take. Management UI lands in a later release — for now, skills are created from chat."
      collapsible
      defaultCollapsed
      badge={
        <StatusPill tone="muted" size="xs">
          Coming soon
        </StatusPill>
      }
    >
      {/* Skills */}
      <div className="rounded-md border border-dashed border-line bg-bg/40 p-4 opacity-80">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-ink-2">Skills</p>
          <StatusPill tone="muted" size="xs">
            Coming soon
          </StatusPill>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted">
          Add, edit and delete reusable instruction packs — e.g. "chase overdue invoices politely in
          Hindi" or "summarise GSTR-1 inputs for the CA". Today you create these by asking Flint in
          chat; a manager here is on the way.
        </p>
      </div>

      {/* Tool groups */}
      <div className="mt-4">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
          Tools the assistant may use
        </p>
        <div className="divide-y divide-line rounded-md border border-line bg-paper opacity-80">
          {TOOL_GROUPS.map((g) => (
            <ToolRow key={g.label} group={g} />
          ))}
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-raised text-muted ring-1 ring-line">
              <Lock size={14} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">Compliance (GST, e-invoice, audit log)</p>
              <p className="mt-0.5 text-xs text-muted">
                Required for correctness — always on, can't be disabled.
              </p>
            </div>
            <StatusPill tone="sky" size="xs">
              Always on
            </StatusPill>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted">
          Per-capability on/off switches arrive with the capability engine in a later release.
        </p>
      </div>
    </FormCard>
  );
}

function ToolRow({ group }: { group: ToolGroup }) {
  const { Icon } = group;
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-bg text-muted ring-1 ring-line">
        <Icon size={14} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">{group.label}</p>
        <p className="mt-0.5 text-xs text-muted">{group.description}</p>
      </div>
      <ToggleStub />
    </div>
  );
}

/** A visually-off, non-interactive toggle for the disabled V1.x rows. */
function ToggleStub() {
  return (
    <span
      aria-hidden
      className="relative h-6 w-11 shrink-0 cursor-not-allowed rounded-full bg-line-2"
      title="Coming soon"
    >
      <span className="absolute left-1 top-1 h-4 w-4 rounded-full bg-paper" />
    </span>
  );
}
